import { NATIVE_CURRENCY_ADDRESS } from "@opensea/app-kit"
import { graphql, useQuery } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  Separator,
  TextBody,
  useToasts,
} from "@opensea/ui-kit"
import { useCallback, useMemo, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import type { WalletDestination } from "../../../components/DestinationWalletSelect/types"
import {
  MAX_PRICE_CHANGE_PERCENTAGE,
  PriceChangeError,
  usePriceValidation,
} from "../../../components/PriceChange"
import { buildPricingLookup } from "../../../components/QuoteReceipt"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useAddress } from "../../../polychain/hooks/useAddress"
import type { SchedulableResponse } from "../../../polychain/hooks/useScheduler"
import type { CurrencyIdentifier } from "../../../providers"
import type { TransactionIdentifier } from "../../../stores/currentTransactionStore"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { CheckoutFlowState } from "../../flow-state"
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted"
import { useInsufficientFundsActionError } from "../../hooks/useErrors"
import {
  useSetItemImageMap,
  useSetItemImageMapFragment,
} from "../../hooks/useSetItemImageMap"
import {
  useSetQuoteItems,
  useSetQuoteItemsFragment,
} from "../../hooks/useSetQuoteItems"
import {
  useSetTotalUsdPrice,
  useSetTotalUsdPriceFragment,
} from "../../hooks/useSetTotalUsdPrice"
import { CheckoutPaymentSection } from "../../PaymentSection/CheckoutPaymentSection"
import { CheckoutToast } from "../../ToastHandler/CheckoutToast/CheckoutToast"
import { CheckoutFlowSkeleton } from "./CheckoutFlowSkeleton"
import { CheckoutReceipt } from "./components/CheckoutReceipt"
import { CheckoutReceiptFragment } from "./components/CheckoutReceipt/CheckoutReceipt.fragment"
import { CheckoutFlowStatus } from "./components/CheckoutStatus/CheckoutStatus"
import { CheckoutTimeline } from "./components/CheckoutTimeline"
import { CheckoutTimeline_quoteFragment } from "./components/CheckoutTimeline/CheckoutTimeline.fragment"
import { CurrencySelector } from "./components/CheckoutTimeline/CurrencySelector"
import { CurrencySelector_relayerFulfillmentFragment } from "./components/CheckoutTimeline/CurrencySelector.fragment"

type Props = Pick<CheckoutFlowState, "items" | "mode" | "slippage">

const CheckoutFlowQuery = graphql(
  `
    query CheckoutFlowQuery(
      $itemQuantities: [ItemQuantityInput!]!
      $address: Address
      $substituteItems: Boolean
    ) {
      buyQuote(
        itemQuantities: $itemQuantities
        address: $address
        substituteItems: $substituteItems
      ) {
        supportsFiatCheckout
        supportsRelayerFulfillment {
          ...CurrencySelector_relayerFulfillment
        }
        itemQuotes {
          ... on ItemQuote {
            pricePerItem {
              token {
                contractAddress
                chain {
                  identifier
                }
              }
            }
          }
          ...useSetQuoteItems
          ...useSetItemImageMap
          ...useSetTotalUsdPrice
        }
        ...CheckoutReceipt
        ...CheckoutTimeline_quote
      }
    }
  `,
  [
    CheckoutReceiptFragment,
    CheckoutTimeline_quoteFragment,
    useSetQuoteItemsFragment,
    useSetItemImageMapFragment,
    useSetTotalUsdPriceFragment,
    CurrencySelector_relayerFulfillmentFragment,
  ],
)

export function CheckoutFlow({ items: itemsWithPricing, slippage }: Props) {
  const t = useTranslations("wallet.CheckoutFlow")
  const address = useAddress()
  const { addCustomToast } = useToasts()
  const initialPrices = useMemo(
    () => buildPricingLookup(itemsWithPricing),
    [itemsWithPricing],
  )
  const { flow, itemQuotes } = useSnapshot(FlowState)
  const mode = (flow as CheckoutFlowState)?.mode
  const isClaim = (flow as CheckoutFlowState)?.isClaim ?? false
  const crossChain = (flow as CheckoutFlowState)?.crossChain ?? false

  const [selectedCurrency, setSelectedCurrency] = useState<
    CurrencyIdentifier | undefined
  >(undefined)
  const [balancesReady, setBalancesReady] = useState(false)
  const [walletDestination, setWalletDestination] = useState<
    WalletDestination | undefined
  >(undefined)

  const _insufficientFundsActionError = useInsufficientFundsActionError()

  const [{ data, fetching, error }] = useQuery({
    query: CheckoutFlowQuery,
    variables: {
      itemQuantities: itemsWithPricing.map(item => ({
        item: {
          tokenId: item.tokenId,
          contractAddress: item.contractAddress,
          chain: item.chain.identifier,
        },
        quantity: item.quantity,
      })),
      address,
      substituteItems: Boolean(slippage),
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  useSetQuoteItems(data?.buyQuote.itemQuotes)
  useSetItemImageMap(data?.buyQuote.itemQuotes)
  const totalUsdPrice = useSetTotalUsdPrice(data?.buyQuote.itemQuotes)
  const priceValidation = usePriceValidation({
    itemsWithPricing,
    flow: "checkout",
    itemQuotes,
  })
  const firstItemQuote = data?.buyQuote.itemQuotes[0]
  const defaultCurrency =
    firstItemQuote?.__typename === "ItemQuote"
      ? {
          contractAddress: firstItemQuote.pricePerItem.token.contractAddress,
          chainIdentifier: firstItemQuote.pricePerItem.token.chain.identifier,
        }
      : {
          contractAddress: NATIVE_CURRENCY_ADDRESS,
          chainIdentifier: itemsWithPricing[0].chain.identifier,
        }

  const [allActionsCompleted] = useAllActionsCompleted()

  const handleAllActionsCompleted = useCallback(
    (
      _response: SchedulableResponse,
      receiptTransactions: TransactionIdentifier[],
    ) => {
      // Only show toast if mode is minimized
      if (mode === "minimized" && receiptTransactions.length > 0) {
        const { itemQuotes, itemImageMap, flow } = FlowState

        addCustomToast(
          () => (
            <CheckoutToast
              crossChain={crossChain}
              flow={flow}
              isClaim={isClaim}
              itemImageMap={itemImageMap}
              itemQuotes={itemQuotes}
              transactionIdentifiers={receiptTransactions}
            />
          ),
          {
            id: "checkout-toast",
            duration: Number.POSITIVE_INFINITY,
          },
        )

        // End the flow after toast is shown
        FlowActions.endFlow()
      }
    },
    [mode, addCustomToast, crossChain, isClaim],
  )

  if (fetching) {
    return (
      <CheckoutFlowSkeleton count={itemsWithPricing.length} isClaim={isClaim} />
    )
  }

  if (!data?.buyQuote || error) {
    throw new Error("There was a problem with checkout.")
  }

  if (
    priceValidation.changedItems.some(
      item =>
        item.priceDifference >
        (item.initialPrice * MAX_PRICE_CHANGE_PERCENTAGE) / 100,
    )
  ) {
    return <PriceChangeError priceValidation={priceValidation} />
  }

  return (
    <>
      {allActionsCompleted ? (
        <BottomSheetBody>
          <CheckoutFlowStatus walletDestination={walletDestination} />
        </BottomSheetBody>
      ) : (
        <>
          <BottomSheetHeader>
            <Flex className="flex-col">
              <BottomSheetHeaderTitle>
                {isClaim ? t("claimTitle") : t("title")}
              </BottomSheetHeaderTitle>
              {isClaim && (
                <TextBody className="mt-1 text-text-secondary" size="sm">
                  {t("claimSubtext")}
                </TextBody>
              )}
            </Flex>
          </BottomSheetHeader>
          <BottomSheetBody>
            <Flex className="size-full flex-col gap-6 md:flex-row md:gap-10">
              <CheckoutReceipt
                hidePricing={isClaim}
                initialPrices={initialPrices}
                quote={data.buyQuote}
              />
              <Separator
                className="hidden h-auto md:block"
                orientation="vertical"
              />
              <CheckoutPaymentSection
                cardSupported={data.buyQuote.supportsFiatCheckout && !isClaim}
                currencySelector={
                  <CurrencySelector
                    className={isClaim ? "" : "mt-8"}
                    defaultCurrency={defaultCurrency}
                    nftChain={itemsWithPricing[0].chain.identifier}
                    onBalancesReady={() => setBalancesReady(true)}
                    onSelect={setSelectedCurrency}
                    onWalletSelect={setWalletDestination}
                    relayerFulfillment={
                      data.buyQuote.supportsRelayerFulfillment
                    }
                    totalUsdPrice={totalUsdPrice ?? 0}
                    walletDestination={walletDestination}
                  />
                }
                hidePaymentMethod={isClaim}
                timeline={
                  <CheckoutTimeline
                    onAllActionsCompleted={handleAllActionsCompleted}
                    priceValidation={priceValidation}
                    quote={data.buyQuote}
                    selectedCurrency={selectedCurrency}
                    selectedCurrencyLoading={!balancesReady}
                    walletDestination={walletDestination}
                  />
                }
              />
            </Flex>
          </BottomSheetBody>
        </>
      )}
    </>
  )
}
